// Route paths registered in routes.tsx, kept as a dependency-free module so
// low-level code (publicPaths.ts, paymentFirst.ts, the Segment bootstrap) can
// read the same literals without pulling in the route tree or page bundles.
//
// PUBLIC_ROUTES render outside the member area: no AuthenticationProvider,
// RequireAuth or NmhProvider, basename "/" (see getBasename in App.tsx).
// MEMBER_ROUTES are children of the authenticated layout route in routes.tsx.

export const PUBLIC_ROUTES = {
  GIFTS: "/gifts",
  GIFTS_CARD: "/gifts/card",
  REDEEM: "/redeem",
  CHECKOUT: "/checkout",
  // Nested under /checkout on purpose so every `=== "/checkout" ||
  // startsWith("/checkout/")` predicate in the app covers the variant for free
  // (basename, i18n namespace skip, early checkout tracking). See paymentFirst.ts.
  CHECKOUT_PAYMENT_FIRST: "/checkout/pf",
} as const;

// Derived, not restated, because react-router requires an absolute CHILD path
// to start with its parent's path and nothing else enforces that. In routes.tsx
// TECHNIQUE is registered inside the TECHNIQUES layout route, so editing one
// string without the other does not merely break /techniques/:id: react-router
// THROWS while rendering the parent as well, which white-screens the entire
// member area, at runtime, with no type error and no failing test to catch it.
// A one-word edit in a module whose whole purpose is to make route names easy
// to edit. Deriving makes the invariant unbreakable rather than documented.
//
// HOME needs no equivalent because its `/:locale?` variant is already built at
// the call site (`${MEMBER_ROUTES.HOME}/:locale?` in routes.tsx), so the two
// move together by construction. This was the only restated pair.
const TECHNIQUES = "/techniques";

export const MEMBER_ROUTES = {
  // Also the navigation target (Index, fallback route); the route itself
  // registers `${HOME}/:locale?`.
  HOME: "/home",
  COLLECTION: "/collection/:id",
  DATE_NIGHT: "/date-night/:id",
  ARTICLE: "/article/:id",
  UPSELL: "/upsell",
  TECHNIQUES,
  TECHNIQUE: `${TECHNIQUES}/:id`,
  // Lean-back mode (OMG-2747): every video on the technique page in sequence.
  // Derived from TECHNIQUE for the same reason TECHNIQUE derives from
  // TECHNIQUES: it is registered inside the TECHNIQUES layout route.
  TECHNIQUE_WATCH: `${TECHNIQUES}/:id/watch`,
  // Never registered on prod builds (routes.tsx gates it on VITE_ENV).
  WHOAMI: "/whoami",
  POST_CHECKOUT: "/post-checkout/:displayName",
  // Never registered on prod builds (routes.tsx gates it on VITE_ENV).
  SIMULATION_TEST: "/simulation-test",
} as const;

export const LOGIN_ROUTE = "/login";

export const AUTH_ROUTES = [
  LOGIN_ROUTE,
  "/signup",
  "/forgot-password",
  "/set-password",
  "/reset-password",
  "/change-password",
];

export const LOGOUT_ROUTES = ["/logout"];

// Locale-prefixed gift/redeem URLs (e.g. /de/gifts, /es_419/redeem) still
// arrive from the marketing footer and older gift emails; routes.tsx redirects
// them to the canonical path with ?lng (OMG-2064).
export const localePrefixed = (path: string) => `/:lng${path}`;
